import { HttpStatusCode } from "axios"

import { removeToken } from "@/hooks/useAuthStorage"

/**
 * Cierre de sesión SÓLO cuando el 401 es de verdad de sesión.
 *
 * Antes: cualquier 401, de cualquier endpoint, borraba el token y recargaba. El problema es que
 * este backend usa 401 también para rechazos de NEGOCIO (herencia del L8), así que operaciones
 * perfectamente normales sacaban al staff al login y se reportaban como "se me venció la sesión":
 *
 *   - `POST crm/user/rechargefree` con una línea que no es de la empresa de la sesión → 401
 *     "El numero no existe" (reporte 20-ago-2026: "cuando quiero activar un sim me saca del
 *     crm"; el backend ya lo pasó a 403, esto es el cinturón del lado del front).
 *   - `GET page/estadoportabilidad` → 401 "Sin actualizaciones" / "Fallido" / "Cancelado", que
 *     son ESTADOS, no fallas de auth. Tenía un parche por URL en `axios.ts`, que se queda porque
 *     además preserva el shape crudo del error para sus consumidores.
 *
 * El criterio ahora es POSITIVO: se cierra la sesión si el 401 viene de una ruta de SESIÓN, o si
 * el `message` es uno de los que emiten los middlewares de auth del backend. Cualquier otro 401
 * se propaga al consumidor para que muestre el mensaje, sin tocar el token. Así no hay que ir
 * apagando incendios de uno en uno cada vez que aparece un 401 de negocio nuevo.
 *
 * Los mensajes están fijados del lado del backend por
 * `tests/Feature/Crm/ContratoDeSesion401Test` (middleware-api-v4): si alguien los reescribe, ese
 * test falla y avisa que hay que actualizar esta lista.
 */

/** Mensajes con los que el backend señala "no hay sesión válida". En minúsculas. */
const MENSAJES_DE_SESION = [
  "token is invalid or expired", // CrmLogged / Seller / EnterpriseLogged / EnsureClientAudience
  "invalid or expired token", // ExternalProviderPermission
  "no autenticado. por favor inicia sesión", // AuthenticationException (bootstrap/app.php)
  "no autenticado", // PosLayer / PosPermission
  "no autorizado", // CrmStaffPermission: sin staff o staff inactivo
  "unauthenticated.", // default de Laravel
]

/**
 * Rutas del ciclo de vida de la sesión. Un 401 aquí es la red de seguridad: si el token muere con
 * un mensaje que no está en la lista, la comprobación de sesión lo detecta igual.
 *
 * Es UNA sola ruta a propósito: `/crm/staffs/me` es el único sondeo de sesión que este front hace
 * de verdad (`useUser`). No se listan `/user/me/tokenisalive` ni `/crm/staffs/logout` porque NADIE
 * los llama desde `src/` — una lista más larga sugeriría una cobertura triple que no existe. Si
 * algún día se cablea un ping a `tokenisalive`, va aquí.
 *
 * ⚠️ Límite conocido de la red: `useUser` refresca cada **5 min y sólo en foreground**
 * (`refetchInterval: 5*60*1000`, `refetchIntervalInBackground: false`). Con un token muerto cuyos
 * 401 no traigan un mensaje de la lista, el operador puede ver hasta 5 minutos de "Tu sesión
 * expiró…" en toasts antes de que el sondeo lo redirija. Es la contracara de no cerrar sesión con
 * cualquier 401: antes el cierre era inmediato y por eso mismo se disparaba cuando no debía.
 */
const RUTAS_DE_SESION = ["/crm/staffs/me"]

/**
 * ¿Este error significa "tu sesión ya no sirve"? Pura a propósito: es la decisión que antes vivía
 * pegada a `window.location.reload()` y por eso no se podía probar.
 */
export function esFallaDeSesion(error: unknown): boolean {
  const e = error as {
    response?: { status?: number; data?: { message?: unknown } }
    config?: { url?: string }
  }

  if (e?.response?.status !== HttpStatusCode.Unauthorized) return false

  const url = e?.config?.url ?? ""
  if (RUTAS_DE_SESION.some((ruta) => url.includes(ruta))) return true

  const message = e?.response?.data?.message
  if (typeof message !== "string") return false

  return MENSAJES_DE_SESION.includes(message.trim().toLowerCase())
}

export const validateUnauthorized = (error: unknown) => {
  if (!esFallaDeSesion(error)) return

  removeToken()
  window.location.reload()
}
